import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { withStyles } from '@mui/styles'
import stylesheet from '../patients/detail/detail-container.stylesheet'
import { ClassNameMap } from '@mui/styles'
import ListAltIcon from '@mui/icons-material/ListAlt';
import Person2Icon from '@mui/icons-material/Person2';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import BusinessIcon from '@mui/icons-material/Business';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import EventNoteIcon from '@mui/icons-material/EventNote';
import ForumIcon from '@mui/icons-material/Forum';
import { Stack, Typography, Box, Badge } from '@mui/material';
import ExpandCircleDownIcon from '@mui/icons-material/ExpandCircleDown';
import Helper from '../../utils/helper';
import { useCurrentUser } from '../../utils/use-current-user';
import { messagingService } from '../../services/messaging.service';

interface IProps {
  classes: ClassNameMap;
  defaultMenuSize?: number;
  isPatient?: boolean
  onToggle?: () => void;
}

const MenuItem: React.FC<{
  href?: string;
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
  classes: ClassNameMap;
  onClick?: () => void;
  badge?: number;
}> = ({ href, icon, label, isActive, classes, onClick, badge }) => {
  const handleClick = (e: React.MouseEvent) => {
    if (onClick) {
      e.preventDefault();
      onClick();
    }
  };

  const iconEl = React.cloneElement(icon as React.ReactElement, {
    className: classes.taskSlidebarMenu,
  });

  return (
    <a
      href={href || '#'}
      onClick={handleClick}
      style={{ textDecoration: 'none', color: 'inherit' }}
    >
      <Stack direction={'column'} alignItems='center' sx={{ '&:hover': { cursor: 'pointer' } }}>
        {badge && badge > 0
          ? <Badge badgeContent={badge} color="error" max={99}
              sx={{ '& .MuiBadge-badge': { fontSize: '0.5rem', height: 14, minWidth: 14, padding: '0 3px' } }}>
              {iconEl}
            </Badge>
          : iconEl}
        <Typography
          className={isActive ? classes.slideMenuTitleActive : classes.slideMenuTitle}
          align='center'
          sx={isActive ? { color: 'var(--color-primary) !important' } : {}}
        >
          {label}
        </Typography>
      </Stack>
    </a>
  );
};

const SlidebarComponent: React.FC<IProps> = ({
  classes,
  defaultMenuSize = 3.1,
  isPatient = false,
  onToggle
}) => {
  const url = useLocation();
  const history = useHistory()
  const { isProvider } = useCurrentUser();

  // "My Messages" waiting count for the nav badge (PORTAL_MESSAGING_ADR D13).
  // Physician-only, so skip the fetch (and its 403) for anyone else. Refetches on
  // navigation, so answering a message and moving on refreshes the badge.
  const [awaitingCount, setAwaitingCount] = React.useState(0);
  React.useEffect(() => {
    if (!isProvider) return;
    let alive = true;
    const load = () => messagingService.getAwaitingWorklist()
      .then((r) => { if (alive) setAwaitingCount(r.awaiting_count); })
      .catch(() => { /* a nav badge must never surface an error */ });
    void load();
    // Refresh the moment the inbox answers something inline, not just on nav.
    window.addEventListener('messages:changed', load);
    return () => { alive = false; window.removeEventListener('messages:changed', load); };
  }, [isProvider, url.pathname]);

  const tabs = ['/patient-list', '/', '/all-tasks', '/my-tasks', '/my-messages', '/top-tasks', '/clinic-visits', '/providers', '/sites', '/rpm-dashboard', '/encounters']

  const handleNavigate = (path: string) => {
    // window.history.pushState({}, '', path);
    history.push(path);
  };

  return (
    <Box sx={{ position: 'relative', height: 'calc(100vh - 48px)', borderRight: '0.12rem solid #e1e1e1' }}>
      {/* Scrollable menu items */}
      <Box sx={{
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        '&::-webkit-scrollbar': { width: '0px' },
      }}>
      <Stack direction={'column'} spacing={1}
        sx={{
          display: 'flex',
          padding: `${tabs.includes(window.location.pathname)
            ? '0.8rem 0rem 0rem 0rem'
            : '0.3rem 0rem 0rem 0rem'
            }`,
          zIndex: 1,
          alignItems: 'center',
          '&:hover': { cursor: 'pointer' },
          paddingBottom: '1rem',
        }}>
        <MenuItem
          //  href="/patient-list"
          onClick={() => handleNavigate('/patient-list')}
          icon={<Person2Icon fontSize='small' className={classes.taskSlidebarMenu} />}
          label="Patients"
          isActive={url.pathname === '/patient-list' || (Helper.isAIDA() && url.pathname === '/')}
          classes={classes}
        />

        {Helper.isAIDA() &&
          <MenuItem
            onClick={() => handleNavigate('/clinic-visits')}
            icon={<Person2Icon fontSize='small' className={classes.taskSlidebarMenu} />}
            label="Clinic visits"
            isActive={url.pathname === '/clinic-visits'}
            classes={classes}
          />
        }
        {/* One task view, not three — three overlapping queues is the clutter this
            deployment is shedding. RD first chose "My tasks", then switched to
            "All tasks" (2026-08-08): tasks here are not assigned per-nurse, so a
            queue scoped to the logged-in user is the wrong frame for this client. */}
        {!Helper.isAIDA() &&
          <MenuItem
            icon={<ListAltIcon fontSize='small' className={classes.taskSlidebarMenu} />}
            label="Top daily tasks"
            isActive={url.pathname === '/top-tasks'}
            classes={classes}
            onClick={() => handleNavigate('/top-tasks')}
          />
        }

        {!Helper.isAIDA() &&
          <MenuItem
            onClick={() => handleNavigate('/my-tasks')}
            icon={<ListAltIcon fontSize='small' className={classes.taskSlidebarMenu} />}
            label="My tasks"
            isActive={url.pathname === '/my-tasks' || url.pathname === '/'}
            classes={classes}
          />
        }

        <MenuItem
          onClick={() => handleNavigate('/all-tasks')}
          icon={<ListAltIcon fontSize='small' className={classes.taskSlidebarMenu} />}
          label="All tasks"
          isActive={url.pathname === '/all-tasks'}
          classes={classes}
        />

        {!Helper.isAIDA() && isProvider &&
          <MenuItem
            onClick={() => handleNavigate('/my-messages')}
            icon={<ForumIcon fontSize='small' className={classes.taskSlidebarMenu} />}
            label="My Messages"
            isActive={url.pathname === '/my-messages'}
            classes={classes}
            badge={awaitingCount}
          />
        }

        <MenuItem
          onClick={() => handleNavigate('/providers')}
          icon={<MedicalServicesIcon fontSize='small' className={classes.taskSlidebarMenu} />}
          label="Providers"
          isActive={url.pathname === '/providers'}
          classes={classes}
        />

        {/* Sites is site-administration, not clinical work — out for this
            deployment. Providers stays (RD 2026-08-08). */}
        {!Helper.isAIDA() &&
          <MenuItem
            onClick={() => handleNavigate('/sites')}
            icon={<BusinessIcon fontSize='small' className={classes.taskSlidebarMenu} />}
            label="Sites"
            isActive={url.pathname === '/sites'}
            classes={classes}
          />
        }

        {!Helper.isAIDA() &&
          <MenuItem
            onClick={() => handleNavigate('/rpm-dashboard')}
            icon={<MonitorHeartIcon fontSize='small' className={classes.taskSlidebarMenu} />}
            label="RPM Billing"
            isActive={url.pathname === '/rpm-dashboard'}
            classes={classes}
          />
        }

        {!Helper.isAIDA() &&
          <MenuItem
            onClick={() => handleNavigate('/revops')}
            icon={<ReceiptLongIcon fontSize='small' className={classes.taskSlidebarMenu} />}
            label="Revenue & Ops"
            isActive={url.pathname === '/revops'}
            classes={classes}
          />
        }

        {/* AIDA gets the test-lifecycle view alone, on its own route — the rest of
            the RevOps console (worklist, ERA, revenue, meds) is not theirs. */}
        {Helper.isAIDA() &&
          <MenuItem
            onClick={() => handleNavigate('/test-tracker')}
            icon={<ReceiptLongIcon fontSize='small' className={classes.taskSlidebarMenu} />}
            label="Test tracker"
            isActive={url.pathname === '/test-tracker'}
            classes={classes}
          />
        }

        {!Helper.isAIDA() &&
          <MenuItem
            onClick={() => handleNavigate('/encounters')}
            icon={<EventNoteIcon fontSize='small' className={classes.taskSlidebarMenu} />}
            label="Encounters"
            isActive={url.pathname === '/encounters'}
            classes={classes}
          />
        }
      </Stack>
      </Box>

      {onToggle && (
        <Box sx={{ width: '100%' }}>
          <ExpandCircleDownIcon
            onClick={onToggle}
            sx={{
              position: 'absolute',
              right: '-0.5rem',
              top: tabs.includes(window.location.pathname) ? '0.8rem' : '0.4rem',
              fontSize: '1rem !important',
              fill: 'var(--color-text-secondary) !important',
              cursor: 'pointer !important',
              zIndex: 1000,
              transform: defaultMenuSize > 0 ? 'rotate(90deg) !important' : 'rotate(270deg) !important',
              '&:hover': {
                fill: 'var(--color-primary) !important',
                cursor: 'pointer !important'
              }
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default withStyles(stylesheet)(SlidebarComponent);